import { useCallback, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { VariablePicker } from './variable-picker.js';

/**
 * Rich text editor for email bodies (Quill under the hood).
 *
 * The toolbar offers bold/italic/underline/lists/links/images and a custom
 * "Insert variable" picker that drops `{{var}}` tokens at the caret. Output
 * is HTML, which the server's interpolator strips/processes the same way as
 * raw text — see template-resolver.ts.
 */

export interface EmailBodyEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** When true, hide the toolbar — useful for SMS / TTS surfaces that
   *  want the variable picker but no rich formatting. */
  plainText?: boolean;
}

const RICH_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    ['clean'],
  ],
};

const PLAIN_MODULES = { toolbar: false };

export function EmailBodyEditor({
  value,
  onChange,
  placeholder,
  plainText,
}: EmailBodyEditorProps): JSX.Element {
  const ref = useRef<ReactQuill>(null);

  const handleInsertVariable = useCallback((token: string) => {
    const editor = ref.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    editor.insertText(range?.index ?? editor.getLength(), token, 'user');
    editor.setSelection((range?.index ?? editor.getLength()) + token.length, 0);
  }, []);

  const modules = useMemo(() => (plainText ? PLAIN_MODULES : RICH_MODULES), [plainText]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <VariablePicker onInsert={handleInsertVariable} />
        <span className="text-xs text-ink-muted">
          Tokens like {'{{first_name}}'} are filled in when each message is sent.
        </span>
      </div>
      <div className="bg-card border border-border rounded-md">
        <ReactQuill
          ref={ref}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}
